import { Badge } from '@/components/ui/badge';
import { getLabelValue } from '@/utils/getLabelValue';
import { LogIn, MapPinned, UserCheck, UserMinus, UserRoundX } from 'lucide-react';
import { useOnsiteStatsAll } from '../api/get-onsite-stats-all';
import { useSettingsStore } from '@/stores/settingsStore';
import { EventStats } from '@/features/desktop/types/event-stats';
import { useNavigate } from 'react-router-dom';

import { Skeleton } from '@/components/ui/skeleton';

export type OnsiteStatus = 'Arrived' | 'Exited' | 'Not_Arrived' | 'Onsite';

const statusClasses: Record<OnsiteStatus, string> = {
  Arrived: 'text-green-700 font-semibold bg-green-50',
  Exited: 'text-blue-700 font-semibold bg-blue-50',
  Not_Arrived: 'text-red-700 font-semibold bg-red-50',
  Onsite: 'text-yellow-700 font-semibold bg-yellow-50',
  Cancelled: 'text-gray-700 font-semibold bg-gray-50',
} as Record<OnsiteStatus, string>;

const statusIcons: Record<OnsiteStatus, JSX.Element> = {
  Arrived: <UserCheck className="w-6 h-6" />,
  Exited: <LogIn className="w-6 h-6" />,
  Not_Arrived: <UserRoundX className="w-6 h-6" />,
  Onsite: <MapPinned className="w-6 h-6" />,
  //Cancelled: <UserMinus className="w-6 h-6" />,
};

const statusToFilterMap: Record<OnsiteStatus, string> = {
  Arrived: 'arrived',
  Exited: 'exited',
  Not_Arrived: 'notArrived',
  Onsite: 'onsite',
  //Cancelled: 'cancelled',
};

export function Statistics() {
  const currentEventId = useSettingsStore(state => state.eventId);
  const navigate = useNavigate();

  const statsQuery = useOnsiteStatsAll({
    eventId: currentEventId ?? 0,
    config: {
      enabled: !!currentEventId,
    },
  });

  function computeStatusCounts(event: EventStats): Record<OnsiteStatus, number> {
    return {
      Arrived: Object.keys(event.stats.arrived).length,
      Exited: Object.keys(event.stats.exit).length,
      Onsite: Object.keys(event.stats.onsite).length,
      Not_Arrived: event.stats.notArrived,
      //Cancelled: event.stats.cancelled,
    };
  }

  const handleBadgeClick = (status: OnsiteStatus, eventId: number) => {
    navigate(`/checkin/attendee-list?status=${statusToFilterMap[status]}&eventId=${eventId}`);
  };

  return (
    <div className="w-full h-full space-y-4 p-4">
      <div className="space-y-2">
        <h4 className="font-medium text-md leading-none">{getLabelValue('StatsTitle')}</h4>
        <p className="text-sm text-muted-foreground">{getLabelValue('StatsDescription')}</p>
      </div>
      {statsQuery.isLoading && (
        <div className="flex flex-col gap-2 justify-center h-full items-center overflow-hidden">
          <Skeleton className="w-full h-32 " />
          <Skeleton className="w-full h-32 " />
          <Skeleton className="w-full h-32 " />
          <Skeleton className="w-full h-32 " />
        </div>
      )}

      <div className="space-y-2">
        {statsQuery.data?.map((event: EventStats) => {
          const statusCounts = computeStatusCounts(event);
          const hasExited = statusCounts.Exited > 0;

          const visibleStatuses: OnsiteStatus[] = ['Arrived', 'Not_Arrived'];
          if (hasExited) {
            visibleStatuses.push('Onsite');
            visibleStatuses.push('Exited');
          }

          if (!Object.values(statusCounts).some(count => count > 0)) {
            return null;
          }
          return (
            <div className="flex flex-col gap-2 p-4 border-b last:border-0 bg-card rounded-md" key={event.eventId}>
              <h3 className="text-lg font-semibold">{event.eventName}</h3>
              <div className="flex flex-wrap gap-2">
                {visibleStatuses.map(status => (
                  <div key={status} onClick={() => handleBadgeClick(status as OnsiteStatus, event.eventId)} className="flex flex-col items-center gap-1 p-1 rounded text-sm">
                    <span className="font-semibold text-[16px]">{getLabelValue(status)}</span>
                    <Badge className={`flex items-center gap-1 p-3 min-w-16 ${statusClasses[status as OnsiteStatus]}`}>
                      {statusIcons[status as OnsiteStatus]}
                      <span className='text-base'>{statusCounts[status]}</span>
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
